import classNames from "classnames";
import { t } from "@lingui/macro";
import { useRouter } from "next/router";
import { PageHead } from "src/molecules/PageHead";
import { useLingui } from "@lingui/react";
import { getQueryString } from "src/modules/page/utils";
import {
  deleteSessionStorageItem,
  getSessionStorageJSON,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import {
  GATED_POPUP_REDIRECT_DATA,
  GATED_POPUP_SOURCE,
} from "src/constants/gatedItem";
import { useCallback, useEffect, useMemo } from "react";
import { Login } from "./components/Login";
import { Locale } from "src/lib/Intl";
import { Register } from "./components/Register";
import styles from "./LoginOrRegisterView.module.scss";
import { Tabs } from "./components/Tabs";
import { useSegment } from "src/hooks/useSegment";
import { segment, SignEvent, SignLocation } from "src/tracking/segment";

type LoginOrRegisterViewProps = {
  locale: Locale;
  type: string;
};

function LoginOrRegisterView({
  locale,
  type,
}: LoginOrRegisterViewProps): JSX.Element {
  const { i18n } = useLingui();
  const { query } = useRouter();
  const { continueAsGuest } = useSegment();

  const signLocation =
    type === "sign-in" ? SignLocation.SIGN_IN_PAGE : SignLocation.SIGN_UP_PAGE;

  const { registerUrl, signInUrl, isCheckoutPath } = useMemo(() => {
    const queryString = getQueryString(query, ["type"]);
    const redirectTo = query.redirect_to as string | undefined;

    return {
      registerUrl: queryString ? `/register?${queryString}` : "/register",
      signInUrl: queryString ? `/sign-in?${queryString}` : "/sign-in",
      isCheckoutPath:
        redirectTo?.includes("cart") || redirectTo?.includes("checkout"),
    };
  }, [query]);

  const handleRegisterClick = useCallback(() => {
    segment.signHandler(SignEvent.SIGN_UP_STARTED, {
      location: signLocation,
    });

    const gatedRedirectStorage: { gatedActionType?: string } =
      getSessionStorageJSON(GATED_POPUP_REDIRECT_DATA) || {};

    if (gatedRedirectStorage.gatedActionType) {
      gatedRedirectStorage.gatedActionType = "Register";

      setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, gatedRedirectStorage);
    }
  }, [signLocation]);

  const handleLoginClick = useCallback(() => {
    segment.signHandler(SignEvent.SIGN_IN_STARTED, {
      location: signLocation,
    });

    const gatedRedirectStorage: { gatedActionType?: string } =
      getSessionStorageJSON(GATED_POPUP_REDIRECT_DATA) || {};

    if (gatedRedirectStorage.gatedActionType) {
      gatedRedirectStorage.gatedActionType = "Login";

      setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, gatedRedirectStorage);
    }
  }, [signLocation]);

  const handleContinueAsGuest = useCallback(() => {
    void continueAsGuest();
  }, [continueAsGuest]);

  useEffect(() => {
    if (query?.source !== GATED_POPUP_SOURCE) {
      deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
    }
  }, [query]);

  return (
    <div className={styles.root} data-testid="login-page">
      <PageHead title={t(i18n)`Sign in | Otrium`} />
      <Tabs
        type={type}
        signInUrl={signInUrl}
        registerUrl={registerUrl}
        handleLoginClick={handleLoginClick}
        handleRegisterClick={handleRegisterClick}
        i18n={i18n}
      />
      <div className={styles.panels}>
        <div
          className={classNames(styles.panel, {
            [styles.active]: type === "sign-in",
          })}
        >
          <Login
            isCheckoutPath={isCheckoutPath}
            i18n={i18n}
            handleRegisterClick={handleRegisterClick}
            registerUrl={registerUrl}
            handleContinueAsGuest={handleContinueAsGuest}
          />
        </div>
        <div
          className={classNames(styles.panel, {
            [styles.active]: type === "register",
          })}
        >
          <Register
            isCheckoutPath={isCheckoutPath}
            i18n={i18n}
            locale={locale}
            handleLoginClick={handleLoginClick}
            signInUrl={signInUrl}
            handleContinueAsGuest={handleContinueAsGuest}
          />
        </div>
      </div>
    </div>
  );
}
export { LoginOrRegisterView };
